import { authFetch } from './authFetch';
import { requestJsonLoyalty } from './loyaltyMembersApi';
/**
 * Reward Catalog API Service
 * Base URL: REACT_APP_API_ENDPOINT_LOYALTY
 * All endpoints: /api/v1/loyalty/rewards/*
 *
 * One dollar-off reward per program, created at program setup only.
 * Supported operations: list, get, update, toggle.
 */

import type { Reward } from '../components/loyalty/data/types';

const LOYALTY_BASE = process.env.REACT_APP_API_ENDPOINT_LOYALTY || 'http://localhost:4004';

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const stored = localStorage.getItem('CREDENTIALS');
    if (stored) {
      const creds = JSON.parse(stored);
      const token = creds?.accessToken || creds?.access_token;
      if (token && typeof token === 'string' && token.trim()) {
        headers['Authorization'] = `Bearer ${token.trim()}`;
      }
      const merchantId = creds?.merchantId;
      if (merchantId && typeof merchantId === 'string' && merchantId.trim()) {
        headers['x-brand-id'] = merchantId.trim();
      }
    }
  } catch {
    // ignore parse errors
  }
  return headers;
}

async function handleResponse<T = any>(response: Response): Promise<T> {
  const text = await response.text();
  let json: any;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response: ${text.substring(0, 200)}`);
  }
  if (!response.ok) {
    const errorMessage = json?.error?.message || json?.message || json?.error || `Request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }
  if (json.success === false) {
    const errorMessage = json?.error?.message || json?.message || 'Request failed';
    throw new Error(errorMessage);
  }
  return json;
}

// ─── Payload Types ──────────────────────────────────────────────

export interface UpdateRewardPayload {
  name?: string;
  points_cost?: number;
  value_per_points?: number;
  max_discount_cap?: number | null;
  delivery_time?: 'sameday' | 'daybefore' | 'weekbefore' | null;
  grace_period?: number | null;
}

export interface ListRewardsParams {
  isautomatedreward?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface RewardsListResponse {
  success: boolean;
  data: {
    rewards: Reward[];
  };
}

export interface RewardDetailResponse {
  success: boolean;
  data: Reward;
}

// Backend field is `max_redemption_amount`; the FE keeps calling it `max_discount_cap`.
function parseCap(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapApiReward<T extends Record<string, any>>(raw: T): T {
  if (!raw || typeof raw !== 'object') return raw;
  const { max_redemption_amount, ...rest } = raw;
  return { ...rest, max_discount_cap: parseCap(max_redemption_amount ?? rest.max_discount_cap) } as unknown as T;
}

function toApiUpdateBody(payload: UpdateRewardPayload): Record<string, unknown> {
  const { max_discount_cap, ...rest } = payload;
  const body: Record<string, unknown> = { ...rest };
  if (max_discount_cap !== undefined) {
    body.max_redemption_amount = max_discount_cap === null ? null : Math.round(Number(max_discount_cap) * 100) / 100;
  }
  return body;
}

// ─── API: List Rewards ──────────────────────────────────────────
export const listRewards = async (params?: ListRewardsParams): Promise<RewardsListResponse> => {
  const query = new URLSearchParams();
  if (params?.isautomatedreward !== undefined) query.set('isautomatedreward', String(params.isautomatedreward));
  if (params?.sort) query.set('sort', params.sort);
  if (params?.order) query.set('order', params.order);
  const qs = query.toString();
  const response = await authFetch(`${LOYALTY_BASE}/api/v1/loyalty/rewards${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const json = await handleResponse<RewardsListResponse>(response);
  if (Array.isArray(json?.data?.rewards)) json.data.rewards = json.data.rewards.map(mapApiReward);
  return json;
};

// ─── API: Get Reward Detail ─────────────────────────────────────
export const getRewardDetail = async (rewardId: string): Promise<RewardDetailResponse> => {
  const response = await authFetch(`${LOYALTY_BASE}/api/v1/loyalty/rewards/${rewardId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const json = await handleResponse<RewardDetailResponse>(response);
  if (json?.data) json.data = mapApiReward(json.data);
  return json;
};

// ─── API: Update Reward ─────────────────────────────────────────
export const updateReward = async (rewardId: string, payload: UpdateRewardPayload): Promise<RewardDetailResponse> => {
  const json = (await requestJsonLoyalty(
    `${LOYALTY_BASE}/api/v1/loyalty/rewards/${rewardId}`,
    toApiUpdateBody(payload),
    'PUT',
  )) as RewardDetailResponse;
  if (json?.data) json.data = mapApiReward(json.data);
  return json;
};

